import React, { useCallback, useEffect, useState } from 'react';
import { User } from '../types';
import { USER_TYPE } from '../CONSTANTS';
import { getUserInfo } from '../libs/db-lib';
import { authStorage } from '../libs/utils-ts';
import { fetchAuthSession, signOut, signIn } from 'aws-amplify/auth';

export interface UserContextInterface {
  user: User | null;
  error: { error: string } | null;
  updateUser: (user: User | null) => void;
  fetchUser: () => void;
  fetchUserAndAuth: (username: string, password: string) => void;
  clearError: () => void;
  updateError: (error: string) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSite: boolean;
  isOEM: boolean;
  isCPO: boolean,
  isMO: boolean,
  partnerID: string;
}

// create the language context with default selected language
export const UserContext = React.createContext<UserContextInterface>({
  user: null,
  error: null,
  updateUser: (user: User | null) => null,
  fetchUser: () => null,
  fetchUserAndAuth: () => null,
  clearError: () => null,
  updateError: (error: string) => null,
  isAuthenticated: false,
  isAdmin: false,
  isSite: false,
  isOEM: false,
  isCPO: false,
  isMO: false,
  partnerID: '',
});

export function useUser() {
  return React.useContext(UserContext);
}

type UserProviderType = {
  children: JSX.Element;
};

export function UserProvider({ children }: UserProviderType) {

  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<{
    error: string;
  } | null>(null);

  const updateUser = useCallback((newUser: User | null) => {
    setUser(newUser);
  },[]);

  const fetchUser = useCallback(async () => {
    const response = await getUserInfo().catch((err) => {
      setError(err);
      return null;
    });

    if (response?.error) {
      setError(response);
    } else {
      updateUser(response || null);
    }
  }, [updateUser]);

  const fetchUserAndAuth = useCallback(
    async (username: string, password: string) => {
      const response = await getUserInfo().then((response) => {
        if (response?.authProvider === 'COGNITO') {
          signIn({
            username: username,
            password: password,
          });
        }
        return response;
      });

      if (response?.error) {
        setError(response);
      } else {
        updateUser(response || null);
      }
    },
    [updateUser]
  );

  useEffect(() => {
    const IdToken = authStorage.getLoginTokens()?.IdToken;
    if (!user && IdToken && !error) {
      fetchUser();
    }
  }, [user, fetchUser,error]);

  function clearError() {
    setError(null);
  }

  function updateError(error: string) {
    setError({ error });
  }

  const handleLogout = async () => {
    authStorage.removeLoginTokens();
    updateUser(null);
    clearError();
  };

  const isAdmin = user?.userType.includes('ADMIN') || false;
  const isSite  = user?.userType === USER_TYPE.SITE_ADMIN || false;
  const isOEM   = user?.userType === USER_TYPE.OEM_ADMIN || false;
  const isMO    = user?.userType === USER_TYPE.MO_ADMIN || false;
  const isCPO   = user?.userType === USER_TYPE.CPO_ADMIN || false;

  const provider = {
    user,
    error,
    updateUser,
    fetchUser,
    fetchUserAndAuth,
    clearError,
    updateError,
    isAuthenticated: !!user,
    isAdmin,
    isSite,
    isOEM,
    isCPO,
    isMO,
    partnerID: user?.partnerID || '',
    handleLogout,
  };

  return (
    <UserContext.Provider value={provider}>{children}</UserContext.Provider>
  );
}
